import styled from "@emotion/styled";
import { lodestarColors, ursaColors } from "@smartsheet/design-tokens";
import React, { forwardRef } from "react";
import { HTMLAttributes } from "react";
import { sizes } from "../../../constants/spacing";
import { RequiredThemeKeys, themed } from "../../../util/theme";
import { useCombinedThemeMode } from "../../../util/theme/useCombinedThemeMode";

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface MenuSeparatorProps extends HTMLAttributes<HTMLHRElement> {}

export interface StyledMenuSeparatorProps extends MenuSeparatorProps {
    themMode: RequiredThemeKeys;
}

export const menuSeparatorTheme = themed({
    color: {
        "lodestar-light": lodestarColors.neutralLight10,
        "ursa-light": ursaColors.neutral60,
    },
    height: {
        "lodestar-light": "2px",
        "ursa-light": "1px",
    },
    gap: {
        // 11 * 2 + 2 = 24px total
        "lodestar-light": "11px",
        // 12 * 2 + 1 = 25px total and that's fine per UX.
        "ursa-light": "12px",
    },
    borderRadius: {
        "lodestar-light": "0px",
        "ursa-light": "3px",
    },
});

const StyledMenuSeparator = styled("hr")<StyledMenuSeparatorProps>`
    display: block;
    height: ${({ themMode }) => menuSeparatorTheme.height(themMode)};
    background-color: ${({ themMode }) => menuSeparatorTheme.color(themMode)};
    margin: ${({ themMode }) => menuSeparatorTheme.gap(themMode)} ${sizes.medium}px;
    padding: 0;
    border: none;
    border-radius: ${({ themMode }) => menuSeparatorTheme.borderRadius(themMode)};
`;

/**
 * https://www.w3.org/TR/wai-aria-1.2/#separator
 */
export const MenuSeparator = forwardRef<HTMLHRElement, MenuSeparatorProps>((props, ref) => {
    const themeKey = useCombinedThemeMode();

    return <StyledMenuSeparator {...props} ref={ref} role="separator" themMode={themeKey} />;
});

MenuSeparator.displayName = "MenuSeparator";
